/// <reference path="../../js/_ResizeObserver.ts" />

import Component from '../../js/_Component';

// The minimum width of a column, in pixels.
const BASE_COLUMN_SIZE = 240;

const CNAME = 'c-block-table';

enum SizeRule {
	/**
	 * Indicates each column should be equal width.
	 */
	EVEN,
	/**
	 * Indicates columns should be scaled proportionally to their maximum content width.
	 */
	MAXIMUM,
	/**
	 * Indicates columns should be scaled proportionally to their average content width.
	 */
	AVERAGE,
	/**
	 * Each cell in the table cannot break down.
	 */
	ABSOLUTE,
}

export default class BlockTable extends Component {

	/**
	 * The underlying table element displaying data for this component.
	 */
	private table: HTMLTableElement;

	/**
	 * The (cached) rule assigned to this table for sizing, mostly to avoid string comparisons and
	 * allow setting via an enum rather than ambiguous strings.
	 */
	private sizeRule: SizeRule;

	/**
	 * The assigned size at which this table should break down to mobile mode.
	 */
	private breakdownSize: number;

	/**
	 * The scrolling mode of the table. True is scrolling, false is a list breakdown.
	 */
	private scrolling: boolean;

	/**
	 * The wrapping element used to size the table.
	 */
	private wrapper: HTMLElement;

	constructor(element: HTMLElement) {
		super(element);

		this.scrolling = this.element.dataset.scrolling !== undefined;

		// Keep the table hidden until measurement is taken.
		this.table = this.element.querySelector('table');
		this.table.style.visibility = 'hidden';

		let firstRow: HTMLElement[] = Array.from(this.element.querySelectorAll('thead th'));
		if (firstRow.length == 0) {
			firstRow = Array.from(this.element.querySelectorAll('tbody tr:first-of-type td'))
		}
		this.resetBreakdownSize(firstRow.length * BASE_COLUMN_SIZE);

		switch (this.element.dataset.sizeRule) {
			case 'maximum':
				this.sizeRule = SizeRule.MAXIMUM;
				break;
			case 'average':
				this.sizeRule = SizeRule.AVERAGE;
				break;
			case 'absolute':
				this.sizeRule = SizeRule.ABSOLUTE;
				break;
			default:
				this.sizeRule = SizeRule.EVEN;
				break;
		}

		this.assignHeadings();

		// Wait until the table is physically in view to perform measurement.
		const intersectionObserver = new IntersectionObserver((entries) => {
			if (entries[0].isIntersecting) {
				this.measureTable();
				intersectionObserver.disconnect();
			}
		});
		intersectionObserver.observe(this.element);

		// Apply/remove the mobile styling at the breakdown size.
		this.wrapper = this.element.querySelector(`.${CNAME}--wrapper`);
		const resizeObserver = new ResizeObserver(() => this.handleResize());
		resizeObserver.observe(this.wrapper);
		this.handleResize();
	}

	private handleResize() {
		if (this.wrapper.getBoundingClientRect().width < this.breakdownSize) {
			this.element.classList.add(this.scrolling ? 'm-scroll' : 'm-list');
		} else {
			this.element.classList.remove(this.scrolling ? 'm-scroll' : 'm-list');
		}
	}

	/**
	 * Measures the content size of each cell.
	 */
	private measureTable() {
		this.table.style.height = '0px';
		const contentSizes = Array.from(this.element.querySelectorAll('tr')).map((row: HTMLTableRowElement) => {
			return Array.from(row.querySelectorAll('th, td') as NodeListOf<HTMLElement>).map((cell) => {
				cell.style.whiteSpace = 'nowrap';
				cell.style.display = 'inline-block';
				const contentWidth = cell.clientWidth + 3; // Adding 3px to account for 2px of border space, and an extra 1px for rounding errors.
				cell.style.whiteSpace = '';
				cell.style.display = '';
				return contentWidth;
			});
		});
		this.table.style.height = '';
		this.table.style.visibility = '';

		let firstRow: HTMLElement[] = Array.from(this.element.querySelectorAll('thead th'));
		if (firstRow.length == 0) {
			firstRow = Array.from(this.element.querySelectorAll('tbody tr:first-of-type td'))
		}

		// Reduce minimum table width if column content is smaller than base size.
		let newSize = this.breakdownSize;
		firstRow.forEach((_item, index) => {
			let maxSize = 0;
			contentSizes.forEach((row) => {
				maxSize = Math.max(maxSize, row[index]);
			});
			if (maxSize < BASE_COLUMN_SIZE) {
				// Shrink the space allocated for this column.
				newSize -= BASE_COLUMN_SIZE - maxSize;
			}
		});
		this.resetBreakdownSize(newSize);

		if (this.sizeRule !== SizeRule.EVEN) {
			// Determine a width portion to assign each column based on its content size.
			let totalWidth = 0;
			const maxWidths = firstRow.map((_item, index, items) => {
				let aggregate = 0;
				if (this.sizeRule === SizeRule.MAXIMUM || this.sizeRule === SizeRule.ABSOLUTE) {
					contentSizes.forEach((row) => {
						aggregate = Math.max(aggregate, row[index]);
					});
				} else if (this.sizeRule === SizeRule.AVERAGE) {
					contentSizes.forEach((row) => {
						aggregate += row[index];
					});
					aggregate /= items.length;
				}
				totalWidth += aggregate;
				return aggregate;
			});

			if (this.sizeRule === SizeRule.ABSOLUTE) { // Resize the minimum size of the table to fit all content.
				this.resetBreakdownSize(totalWidth);
			}

			// Assign percentage widths to each column based on the aggregate values.
			firstRow.forEach((head: HTMLElement, index) => {
				head.style.width = `${maxWidths[index] / totalWidth * 100}%`;
			});
		}
	}

	/**
	 * Copies the appropriate heading value to each row.
	 */
	private assignHeadings() {
		const headings = this.element.querySelectorAll('thead th');

		if (headings.length > 0) {
			this.element.querySelectorAll('tbody tr').forEach((row) => {
				row.querySelectorAll('td').forEach((cell, index) => {
					// First element becomes a pseudo-header if the row headings option is enabled.
					if ((index === 0) && (this.element.dataset.rowHeadings !== undefined)) {
						cell.classList.add('m-row-heading');
					} else {
						cell.dataset.heading = headings[index].textContent;
					}
				});
			});
		}
	}

	/**
	 * Updates the minimum width of the table.
	 */
	private resetBreakdownSize(width: number) {
		this.breakdownSize = width;
		// Scrolling tables force a minimum width.
		if (this.scrolling) {
			this.table.style.minWidth = `${this.breakdownSize}px`;
		}
	}
}

Component.register(BlockTable, 'BlockTable');
